import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { statsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePendingReviews } from '../components/ReportReviewCard';
import { doctorEscalationAPI } from '../services/doctorApi';
import { navigatorStats } from '../utils/escalationState';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user }  = useAuth();

  const [stats,    setStats]    = useState({});
  const [loading,  setLoading]  = useState(true);
  // Escalation counts for navigators; null until fetched (shown as an em dash).
  const [esc, setEsc] = useState(null);
  const isNavigator = user?.role === 'HEALTH_NAVIGATOR';
  // AI report reviews waiting on this navigator — a count here, the list is on /escalations?tab=ai.
  const reviews = usePendingReviews(isNavigator);

  useEffect(() => {
    const load = async () => {
      try {
        const dashRes = await statsAPI.dashboard().catch(() => null);
        if (dashRes) setStats(dashRes.data || {});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const roleFallback = user?.role === 'DOCTOR' ? 'Doctor' : 'Navigator';

  // Counts only — the queue itself lives on /escalations. Two light fetches:
  // everything unresolved, and the part assigned to this navigator.
  useEffect(() => {
    if (!isNavigator) return undefined;
    let live = true;
    Promise.allSettled([
      doctorEscalationAPI.list({ view: 'ACTIVE' }),
      doctorEscalationAPI.list({ view: 'ACTIVE', assignedToMe: true }),
    ]).then(([all, mine]) => {
      if (!live) return;
      if (all.status === 'fulfilled' && mine.status === 'fulfilled') {
        setEsc(navigatorStats(all.value.data || [], mine.value.data || []));
      }
    });
    return () => { live = false; };
  }, [isNavigator]);

  // Dynamic page title
  useEffect(() => {
    document.title = 'Dashboard — Previu';
    return () => { document.title = 'Previu — Patient Management'; };
  }, []);

  // With `to`, the whole card is a link to the list behind the figure — the
  // same button + .card--link pattern as the doctor dashboard.
  // Colour follows state, not decoration: a tile is neutral unless it holds a
  // non-default state. `state` is 'critical' (rose, act now) or 'attention'
  // (gold, pending); each also carries a text tag so colour is never the only
  // signal. Callers pass state only when the count is above zero.
  const STATE_STYLE = {
    critical:  { color: 'var(--red)',        badge: 'badge-inactive', tag: 'Act now' },
    attention: { color: 'var(--amber-text)', badge: 'badge-pending',  tag: 'Needs attention' },
  };
  const StatCard = ({ label, value, sub, state, to }) => {
    const st = STATE_STYLE[state];
    const body = (
      <>
        <div className="stat-label">{label}</div>
        <div className="stat-value" style={st ? { color: st.color } : {}}>
          {loading ? '—' : (value ?? '—')}
        </div>
        {sub && <div className="stat-change stat-change--neutral">{sub}</div>}
        {st && <span className={`badge ${st.badge}`} style={{ marginTop: 8 }}>{st.tag}</span>}
      </>
    );
    const cls = `stat-card${state === 'critical' ? ' stat-card--alert' : ''}`;
    if (!to) return <div className={cls}>{body}</div>;
    return (
      <button type="button" className={`${cls} card--link`} onClick={() => navigate(to)}
        aria-label={`${label}: ${loading ? '—' : (value ?? '—')}. View the list.`}>
        {body}
      </button>
    );
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Dashboard' }]}>

      {/* Greeting banner */}
      <div className="fade-in-up" style={{
        background: 'linear-gradient(135deg, var(--brand-dark) 0%, var(--brand-navy) 100%)',
        borderRadius: 'var(--radius-lg)', padding: '28px 32px',
        marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: -30, top: -30, width: 180,
          height: 180, borderRadius: '50%', background: 'rgba(184,132,58,0.1)' }} />
        <div style={{ position: 'absolute', right: 80, bottom: -50, width: 130,
          height: 130, borderRadius: '50%', background: 'rgba(184,132,58,0.06)' }} />
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700,
          color: 'var(--text-on-brand-dark)', marginBottom: 6, position: 'relative' }}>
          {greeting}, {user?.name?.split(' ')[0] || roleFallback}
        </div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', position: 'relative' }}>
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      {/* Escalations — navigators only. Counts here; the queue is on /escalations. */}
      {isNavigator && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: 1 }}>
              Escalations
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/escalations')}>
              View all →
            </button>
          </div>
          <div className="stats-row fade-in-up delay-1">
            <StatCard label="Assigned to me" value={esc?.assigned}
              to="/escalations?queue=mine"
              sub={esc?.toBook > 0
                ? `${esc.toBook} specialist booking${esc.toBook === 1 ? '' : 's'} to make`
                : 'handed over by a doctor'} />
            <StatCard label="Needs response" value={esc?.needsResponse}
              to="/escalations?queue=open"
              sub="not yet acknowledged" state={esc?.needsResponse > 0 ? 'attention' : undefined} />
            <StatCard label="Overdue" value={esc?.overdue}
              to="/escalations?queue=open&filter=overdue"
              sub="past the 2h SLA" state={esc?.overdue > 0 ? 'critical' : undefined} />
            <StatCard label="In progress" value={esc?.inProgress}
              to="/escalations?queue=progress"
              sub="acknowledged, being worked" />
          </div>
        </>
      )}

      {/* Stats row 1 — Patients */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
        Patients
      </div>
      <div className="stats-row fade-in-up delay-1">
        <StatCard label="Total Patients"  value={stats.totalPatients} sub="all registered" to="/patients" />
        <StatCard label="Active"          value={stats.totalActive}   sub="currently enrolled" to="/patients?status=ACTIVE" />
        <StatCard label="New This Month"  value={stats.newThisMonth}  sub={`since 1 ${new Date().toLocaleString('en-IN',{month:'short'})}`} to="/patients?joined=month" />
        <StatCard label="New This Week"   value={stats.newThisWeek}   sub="last 7 days" to="/patients?joined=week" />
      </div>

      {/* Stats row 2 — Appointments */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, marginTop: 8 }}>
        Appointments
      </div>
      <div className="stats-row fade-in-up delay-2">
        <StatCard label="Today"           value={stats.appointmentsToday}     sub="scheduled today"       to="/appointments?when=today" />
        <StatCard label="Pending"         value={stats.appointmentsPending}   sub="awaiting confirmation"  state={stats.appointmentsPending > 0 ? 'attention' : undefined} to="/appointments?status=SCHEDULED" />
        <StatCard label="Confirmed"       value={stats.appointmentsConfirmed} sub="ready to go"            to="/appointments?status=CONFIRMED" />
        <StatCard label="This Month"      value={stats.appointmentsThisMonth} sub="total appointments"     to="/appointments?when=month" />
      </div>

      {/* Recent patients / upcoming appointments / AI reviews — counts only; the lists live on /patients, /appointments and /escalations. */}
      <div className="stats-row fade-in-up delay-3" style={{ gridTemplateColumns: `repeat(${isNavigator ? 3 : 2}, 1fr)` }}>
        <StatCard label="Recent Patients" value={stats.newThisWeek}
          sub="added in the last 7 days" to="/patients?joined=week" />
        <StatCard label="Upcoming Appointments"
          value={stats.appointmentsPending == null && stats.appointmentsConfirmed == null
            ? undefined : (stats.appointmentsPending || 0) + (stats.appointmentsConfirmed || 0)}
          sub="scheduled or confirmed" to="/appointments?when=upcoming" />
        {isNavigator && (
          <StatCard label="AI Actions to Review"
            value={reviews.loading || reviews.failed ? undefined : reviews.items.length}
            sub="reports waiting for your review"
            state={reviews.items.length > 0 ? 'attention' : undefined} to="/escalations?tab=ai" />
        )}
      </div>
    </AppShell>
  );
}

